import * as Sentry from "@sentry/react";
import { diagnosticEventLog } from "./DiagnosticEventLog";
import { throughputTracker } from "./ThroughputTracker";
import type {
  DiagnosticCategory,
  DiagnosticReport,
  DiagnosticReportEnvironment,
  SentryReplayInfo,
} from "./types";

declare const __APP_VERSION__: string;
declare const __COMMIT_SHA__: string;
declare const __BRANCH__: string;

const appVersion =
  typeof __APP_VERSION__ !== "undefined" ? __APP_VERSION__ : "unknown";
const commitSha =
  typeof __COMMIT_SHA__ !== "undefined" ? __COMMIT_SHA__ : "unknown";
const branch = typeof __BRANCH__ !== "undefined" ? __BRANCH__ : "unknown";

function generateReportId(): string {
  const rand = Math.random().toString(36).substring(2, 8);
  const ts = Date.now();
  return `diag-${rand}-${ts}`;
}

// ==========================================
// Sub-collectors
// ==========================================

function collectEnvironment(): DiagnosticReportEnvironment {
  let tokenExpiry: number | null = null;
  let authenticated = false;

  try {
    const kc = (window as unknown as Record<string, unknown>).__keycloak as
      | { authenticated?: boolean; tokenParsed?: { exp?: number } }
      | undefined;
    if (kc) {
      authenticated = !!kc.authenticated;
      tokenExpiry = kc.tokenParsed?.exp ?? null;
    }
  } catch {
    // Keycloak not available
  }

  return {
    userAgent: navigator.userAgent,
    screenWidth: window.innerWidth,
    screenHeight: window.innerHeight,
    devicePixelRatio: window.devicePixelRatio,
    online: navigator.onLine,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    locale: navigator.language,
    documentVisibility: document.visibilityState,
    timeSincePageLoadMs: Math.round(performance.now()),
    appVersion,
    commitSha,
    branch,
    url: window.location.href,
    keycloak: { authenticated, tokenExpiry },
  };
}

async function collectSentryReplay(): Promise<SentryReplayInfo> {
  try {
    const client = Sentry.getClient();
    const replay = client?.getIntegrationByName?.("Replay") as
      | { getReplayId?: () => string | undefined; flush?: () => Promise<void> }
      | undefined;

    if (!replay) {
      return { replayId: null, replayUrl: null, replayActive: false };
    }

    const replayId = replay.getReplayId?.() ?? null;

    if (replayId && replay.flush) {
      try {
        await replay.flush();
      } catch {
        // Flush failure is non-fatal
      }
    }

    const dsn = client?.getDsn?.();
    let replayUrl: string | null = null;
    if (replayId && dsn) {
      const orgSlug = dsn.host.split(".")[0];
      replayUrl = `https://sentry.io/organizations/${orgSlug}/replays/${replayId}/`;
    }

    return { replayId, replayUrl, replayActive: !!replayId };
  } catch {
    return { replayId: null, replayUrl: null, replayActive: false };
  }
}


// ==========================================
// Main collector
// ==========================================

export async function collectDiagnosticReport(
  selectedCategories: Set<DiagnosticCategory>
): Promise<DiagnosticReport> {
  const report: DiagnosticReport = {
    reportId: generateReportId(),
    generatedAt: new Date().toISOString(),
    appVersion,
    commitSha,
    categories: {},
  };

  if (selectedCategories.has("sseState")) {
    report.categories.sseState = diagnosticEventLog.getEntries({ category: "sse" });
  }

  if (selectedCategories.has("eventTimeline")) {
    report.categories.eventTimeline = diagnosticEventLog.getEntries();
  }

  if (selectedCategories.has("networkErrors")) {
    report.categories.networkErrors = diagnosticEventLog.getEntries({ category: "http" });
  }

  if (selectedCategories.has("environment")) {
    report.categories.environment = collectEnvironment();
  }

  if (selectedCategories.has("sentryReplay")) {
    report.categories.sentryReplay = await collectSentryReplay();
  }

  if (selectedCategories.has("widgetState")) {
    report.categories.widgetState = diagnosticEventLog.getEntries({ category: "widget" });
  }

  if (selectedCategories.has("appLifecycle")) {
    report.categories.appLifecycle = diagnosticEventLog.getEntries({ category: "app" });
  }

  if (selectedCategories.has("audioPlayer")) {
    report.categories.audioPlayer = diagnosticEventLog.getEntries({ category: "audio" });
  }

  if (selectedCategories.has("sseState") || selectedCategories.has("eventTimeline") || selectedCategories.has("audioPlayer")) {
    report.throughput = throughputTracker.getSnapshots();
  }

  return report;
}
